/**
 * src/context/AuthContext.js
 * ─────────────────────────────────────────────────────────────────────────────
 * JWT session management.
 * Fixes vs previous version:
 *  • register() surfaces field-level errors from ApiError.detail
 *  • logout() dispatches 'lc:cart-clear' so CartContext can reset
 *  • Listens for 'lc:session-expired' from api.js auto-refresh failure
 *  • profile() call during restore is safely guarded; failures clear tokens
 *  • updateProfile() keeps user state in sync
 */
import React, { createContext, useContext, useState, useEffect } from 'react';
import { authApi, unwrap } from '../services/api';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user,    setUser]    = useState(null);
  const [loading, setLoading] = useState(true);

  // ── Restore session on mount ───────────────────────────────────────────────
  useEffect(() => {
    const token = localStorage.getItem('lc_access');
    if (!token) { setLoading(false); return; }

    authApi.profile()
      .then(res => setUser(unwrap(res)))
      .catch(() => {
        // Token is invalid / expired and refresh also failed
        localStorage.removeItem('lc_access');
        localStorage.removeItem('lc_refresh');
      })
      .finally(() => setLoading(false));
  }, []);

  // ── Listen for forced session expiry (refresh token expired) ──────────────
  useEffect(() => {
    const handler = () => {
      setUser(null);
      window.dispatchEvent(new Event('lc:cart-clear'));
    };
    window.addEventListener('lc:session-expired', handler);
    return () => window.removeEventListener('lc:session-expired', handler);
  }, []);

  // ── Auth actions ──────────────────────────────────────────────────────────

  /**
   * Login — returns the user object on success, throws ApiError on failure.
   * Field-level errors (e.g. inactive account) are in err.detail.
   */
  const login = async (email, password) => {
    const res  = await authApi.login({ email, password });
    const data = unwrap(res);
    localStorage.setItem('lc_access',  data.tokens.access);
    localStorage.setItem('lc_refresh', data.tokens.refresh);
    setUser(data.user);
    return data.user;
  };

  /**
   * Register — returns the new user on success.
   * Re-throws ApiError with .detail containing field errors so forms can
   * surface them: catch(err) { setErrors(getFieldErrors(err)) }
   */
  const register = async (formData) => {
    const res  = await authApi.register(formData);
    const data = unwrap(res);
    localStorage.setItem('lc_access',  data.tokens.access);
    localStorage.setItem('lc_refresh', data.tokens.refresh);
    setUser(data.user);
    return data.user;
  };

  /**
   * Logout — clears tokens, resets user, signals cart to clear.
   */
  const logout = () => {
    localStorage.removeItem('lc_access');
    localStorage.removeItem('lc_refresh');
    setUser(null);
    window.dispatchEvent(new Event('lc:cart-clear'));
  };

  /**
   * Update profile — PATCH /users/profile/ and keep local state in sync.
   */
  const updateProfile = async (formData) => {
    const res  = await authApi.update(formData);
    const data = unwrap(res);
    setUser(data);
    return data;
  };

  return (
    <AuthContext.Provider value={{
      user,
      loading,
      login,
      logout,
      register,
      updateProfile,
      isAdmin:         user?.is_staff  === true,
      isAuthenticated: user !== null,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside <AuthProvider>');
  return ctx;
};
